import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Fade,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Remove as MinimizeIcon,
  Videocam as VideocamIcon,
  Call as CallIcon,
} from '@mui/icons-material';
import { CONTEXTS, SERVICES } from 'figshelf-modules';
import { useCallOverlay } from '../../contexts/CallOverlayContext';
import MessageBubble from '../messages/MessageBubble';
import MessageInput from '../messages/MessageInput';
import MiniProfile from '../messages/MiniProfile';

const { useMessages, useAuth } = CONTEXTS;
const { MessageService } = SERVICES;

/**
 * Individual chat popup window (Facebook-style)
 * Displays at bottom-right of screen
 */
const ChatPopup = ({ 
  conversationId, 
  onClose, 
  onMinimize,
  isMinimized = false,
  position = 0, // Position from right (0 = rightmost)
}) => {
  const { user } = useAuth();
  const { openCall } = useCallOverlay();
  const { user: messagesUser, messagesSocketService, markConversationAsReadLocal } = useMessages();

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [messagesPage, setMessagesPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);

  const [messageInput, setMessageInput] = useState('');
  const [typingUsers, setTypingUsers] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);
  
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastSeenIncomingAtRef = useRef(0);
  const lastSeenIncomingIdRef = useRef(null);

  const upsertLocalMessages = useMemo(() => {
    return (prevMessages, nextMessage) => {
      if (!nextMessage) return prevMessages;
      const base = Array.isArray(prevMessages) ? prevMessages : [];

      const nextId = nextMessage?.id || nextMessage?.messageId;
      const nextClientId = nextMessage?.clientId;

      if (nextId) {
        const idx = base.findIndex((m) => (m?.id || m?.messageId) === nextId);
        if (idx >= 0) {
          const copy = base.slice();
          copy[idx] = { ...copy[idx], ...(nextMessage.data || nextMessage) };
          return copy;
        }
      }

      if (nextClientId) {
        const idx = base.findIndex((m) => m?.clientId && m.clientId === nextClientId);
        if (idx >= 0) {
          const copy = base.slice();
          copy[idx] = { ...copy[idx], ...(nextMessage.data || nextMessage) };
          return copy;
        }
      }

      // Finally check for duplicates by content + timestamp to prevent duplicate messages
      const isDuplicate = base.some((m) => {
        const sameContent = m?.content === nextMessage?.content;
        const sameType = m?.type === nextMessage?.type;
        const sameSender = (m?.sender?.id || m?.senderId) === (nextMessage?.sender?.id || nextMessage?.senderId);
        const sameTime = Math.abs(new Date(m?.createdAt).getTime() - new Date(nextMessage?.createdAt).getTime()) < 5000;
        return sameContent && sameType && sameSender && sameTime;
      });

      if (isDuplicate) {
        return base;
      }

      return [...base, (nextMessage.data || nextMessage)];
    };
  }, []);

  const parseMessageDate = (value) => {
    if (!value) return new Date(0);
    if (value instanceof Date) return value;
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
      const isoWithoutTz = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value);
      return new Date(isoWithoutTz ? `${value}Z` : value);
    }
    return new Date(value);
  };

  const groupedMessages = useMemo(() => {
    if (!messages || messages.length === 0) return [];

    const sortedMessages = [...messages].sort((a, b) => parseMessageDate(a.createdAt) - parseMessageDate(b.createdAt));
    const grouped = [];
    const GROUP_TIME_THRESHOLD = 5 * 60 * 1000;
    const processedDates = new Set();

    for (let i = 0; i < sortedMessages.length; i++) {
      const message = sortedMessages[i];
      const messageDate = parseMessageDate(message.createdAt).toDateString();

      if (!processedDates.has(messageDate)) {
        const today = new Date().toDateString();
        const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toDateString();

        let displayDate;
        if (messageDate === today) {
          displayDate = 'Today';
        } else if (messageDate === yesterday) {
          displayDate = 'Yesterday';
        } else {
          displayDate = parseMessageDate(message.createdAt).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
        }

        grouped.push({
          id: `date-${messageDate}`,
          type: 'date',
          date: displayDate,
          isDateHeader: true,
        });
        processedDates.add(messageDate);
      }

      const prevMessage = i > 0 ? sortedMessages[i - 1] : null;
      const nextMessage = i < sortedMessages.length - 1 ? sortedMessages[i + 1] : null;

      const prevMessageDate = prevMessage ? parseMessageDate(prevMessage.createdAt).toDateString() : null;
      const nextMessageDate = nextMessage ? parseMessageDate(nextMessage.createdAt).toDateString() : null;

      const isSameSenderAsPrev =
        prevMessage &&
        prevMessage.sender?.id === message.sender?.id &&
        prevMessageDate === messageDate;

      const isCloseInTimeToPrev =
        prevMessage &&
        parseMessageDate(message.createdAt) - parseMessageDate(prevMessage.createdAt) < GROUP_TIME_THRESHOLD &&
        prevMessageDate === messageDate;

      const isSameSenderAsNext =
        nextMessage &&
        nextMessage.sender?.id === message.sender?.id &&
        nextMessageDate === messageDate;

      const isCloseInTimeToNext =
        nextMessage &&
        parseMessageDate(nextMessage.createdAt) - parseMessageDate(message.createdAt) < GROUP_TIME_THRESHOLD &&
        nextMessageDate === messageDate;

      const isGroupedWithPrev = Boolean(isSameSenderAsPrev && isCloseInTimeToPrev);
      const isGroupedWithNext = Boolean(isSameSenderAsNext && isCloseInTimeToNext);

      grouped.push({
        ...message,
        sender: message.sender || { id: 'unknown', name: 'User' },
        isFirstInGroup: !isGroupedWithPrev,
        isLastInGroup: !isGroupedWithNext,
        isOnlyInGroup: !isGroupedWithPrev && !isGroupedWithNext,
        isFirstMessage: message.isFirstMessage || false,
      });
    }

    return grouped;
  }, [messages]);

  const lastOutgoingMessageKey = useMemo(() => {
    const currentUserId = user?.id;
    if (!currentUserId || !Array.isArray(messages) || messages.length === 0) return null;

    const sorted = [...messages].sort((a, b) => parseMessageDate(a.createdAt) - parseMessageDate(b.createdAt));
    for (let i = sorted.length - 1; i >= 0; i--) {
      const m = sorted[i];
      if (m?.sender?.id === currentUserId) {
        return m.clientId || m.id;
      }
    }

    return null;
  }, [messages, user]);

  const pinnedMessageIdSet = useMemo(() => {
    const pins = Array.isArray(pinnedMessages) ? pinnedMessages : [];
    return new Set(pins.map((p) => p?.messageId).filter(Boolean));
  }, [pinnedMessages]);

  const handleMessageVisible = (msg) => {
    if (!msg || !conversationId || !messagesSocketService) return;
    const currentUserId = user?.id;
    if (!currentUserId) return;
    if (msg?.sender?.id === currentUserId) return;

    const messageDate = parseMessageDate(msg.createdAt).getTime();
    if (!Number.isFinite(messageDate)) return;

    const recipients = Array.isArray(msg.recipients) ? msg.recipients : [];
    const myRecipient = recipients.find((r) => r?.recipientId === currentUserId);
    if (myRecipient?.status === 'seen') return;

    if (lastSeenIncomingIdRef.current === msg.id) return;
    if (messageDate <= lastSeenIncomingAtRef.current) return;

    lastSeenIncomingAtRef.current = messageDate;
    lastSeenIncomingIdRef.current = msg.id;
    messagesSocketService.setMessageStatus(conversationId, msg.id, 'seen');
  };

  // Load conversation/messages locally (do NOT use global selectConversation so multiple popups can coexist)
  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await MessageService.getConversation(conversationId, 1, 50);
        console.log('ChatPopup - Full API response:', JSON.stringify(data, null, 2));
        console.log('ChatPopup - Conversation name:', data?.name);
        console.log('ChatPopup - Conversation type:', data?.type);
        console.log('ChatPopup - Participants count:', data?.participants?.length);
        console.log('ChatPopup - Participants:', JSON.stringify(data?.participants, null, 2));
        if (cancelled) return;
        const normalized = Array.isArray(data?.messages) ? data.messages : [];
        setConversation(data || { id: conversationId });
        setMessages(normalized);
        setMessagesPage(Number(data?.page || 1));
        const totalPages = Number(data?.totalPages || 1);
        setHasMoreMessages(Boolean((Number(data?.page || 1)) < totalPages));
      } catch (e) {
        console.error('ChatPopup - Error loading conversation:', e);
        if (cancelled) return;
        setConversation({ id: conversationId });
        setMessages([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  // Keep this popup in sync with realtime incoming socket messages
  useEffect(() => {
    if (!conversationId || !messagesSocketService) return;

    const handleIncoming = (data) => {
      if (!data?.conversationId || data.conversationId !== conversationId) return;

      const normalized = data?.data || data;
      setMessages((prev) => upsertLocalMessages(prev, normalized));

      setConversation((prev) => {
        const base = prev || { id: conversationId };
        const nowIso = new Date().toISOString();
        const lastAt = normalized?.createdAt || nowIso;
        return {
          ...base,
          lastMessage: normalized,
          lastMessageAt: lastAt,
          updatedAt: lastAt,
        };
      });

      // If popup is open/active, clear unread and mark as read (so badge clears)
      try {
        const isFromMe = Boolean(user?.id && normalized?.sender?.id && normalized.sender.id === user.id);
        if (!isMinimized && !isFromMe) {
          if (typeof markConversationAsReadLocal === 'function') {
            markConversationAsReadLocal(conversationId);
          }
          MessageService.markAllAsRead(conversationId).catch(() => {});
        }
      } catch {
      }
    };

    const listener = messagesSocketService.addListener('onMessageReceived', handleIncoming);
    return () => {
      messagesSocketService.removeListener('onMessageReceived', listener);
    };
  }, [conversationId, messagesSocketService, isMinimized, user?.id, markConversationAsReadLocal, upsertLocalMessages]);

  // Load pinned messages for this conversation
  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    (async () => {
      try {
        const pinsResp = await MessageService.getPinnedMessages(conversationId);
        const pins = Array.isArray(pinsResp?.pinned) ? pinsResp.pinned : [];
        if (!cancelled) setPinnedMessages(pins);
      } catch {
        if (!cancelled) setPinnedMessages([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  const shouldShowMiniProfile = Boolean(conversation && messages?.some(msg => msg.isFirstMessage));

  // Scroll to bottom when messages change
  useEffect(() => {
    if (!isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isMinimized]);

  // Setup typing indicator listeners
  useEffect(() => {
    if (!conversationId || !messagesSocketService) return;

    const handleUserTyping = (data) => {
      const typingData = data.content || data;
      if (!typingData?.user?.id || typingData.user.id === user?.id) return;

      const userId = typingData.user.id;
      const isTyping = typingData.isTyping;

      setTypingUsers(prev => {
        const updated = { ...prev };
        if (isTyping) {
          updated[userId] = {
            user: typingData.user,
            isTyping: true,
            timestamp: Date.now()
          };
        } else {
          delete updated[userId];
        }
        return updated;
      });

      if (isTyping) {
        setTimeout(() => {
          setTypingUsers(prev => {
            const updated = { ...prev };
            if (updated[userId] && Date.now() - updated[userId].timestamp > 4500) {
              delete updated[userId];
            }
            return updated;
          });
        }, 5000);
      }
    };

    const typingListener = messagesSocketService.addListener('onUserTyping', handleUserTyping);

    return () => {
      messagesSocketService.removeListener('onUserTyping', typingListener);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [conversationId, messagesSocketService, user]);


  const getConversationName = () => {
    console.log('getConversationName - conversation:', conversation);
    console.log('getConversationName - conversation.name:', conversation?.name);
    console.log('getConversationName - user.id:', user?.id);
    
    if (!conversation) return 'Loading...';
    if (conversation.name) {
      console.log('getConversationName - returning conversation.name:', conversation.name);
      return conversation.name;
    }
    
    if (conversation.participants?.length > 0) {
      console.log('getConversationName - looking for other participant, currentUserId:', conversation.currentUserId, 'user.id:', user?.id);
      const otherParticipant = conversation.participants.find(
        p => p.user && p.user.id !== (conversation.currentUserId || user?.id)
      );
      
      console.log('getConversationName - otherParticipant:', otherParticipant);
      
      if (otherParticipant) {
        const name = otherParticipant.nickname ||
               otherParticipant.user?.profile?.displayName ||
               otherParticipant.user?.username ||
               'User';
        console.log('getConversationName - returning participant name:', name);
        return name;
      }
    }
    
    console.log('getConversationName - falling back to Conversation');
    return 'Conversation';
  };

  const getConversationAvatar = () => {
    if (!conversation) return null;
    if (conversation.avatar) return conversation.avatar;
    
    if (conversation.participants?.length > 0) {
      const otherParticipant = conversation.participants.find(
        p => p.user && p.user.id !== (conversation.currentUserId || user?.id)
      );
      
      if (otherParticipant?.user?.profile?.media) {
        const avatar = otherParticipant.user.profile.media.find(m => m.type === 'avatar');
        return avatar?.url;
      }
    }
    
    return null;
  };

  // Handlers for MessageInput component
  const handleMessageChange = (e) => {
    setMessageInput(e.target.value);

    // Emit typing indicator
    if (messagesSocketService && conversationId) {
      messagesSocketService.setTyping(conversationId, { isTyping: true });

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = setTimeout(() => {
        messagesSocketService.setTyping(conversationId, { isTyping: false });
      }, 2000);
    }
  };

  const handleSendMessage = async (content) => {
    try {
      if (!conversationId) return;

      const optimistic = {
        id: `client-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        clientId: `client-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        pending: true,
        status: 'sending',
        conversationId,
        type: 'text',
        content,
        sender: user ? { id: user.id, username: user.username, profile: user.profile } : undefined,
        recipients: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        replyTo: replyingTo,
      };

      setMessages((prev) => [...(Array.isArray(prev) ? prev : []), optimistic]);
      setConversation((prev) =>
        prev
          ? {
              ...prev,
              lastMessage: optimistic,
              lastMessageAt: optimistic.createdAt,
            }
          : prev,
      );

      const sent = await MessageService.sendMessage(
        conversationId,
        content,
        'text',
        {},
        replyingTo ? replyingTo.id : null
      );
      if (sent) {
        setMessages((prev) => {
          const base = Array.isArray(prev) ? prev.slice() : [];
          const idx = base.findIndex((m) => m?.id === optimistic.id || (optimistic.clientId && m?.clientId === optimistic.clientId));
          if (idx >= 0) {
            base[idx] = { ...base[idx], ...(sent.data || sent), pending: false, status: 'sent' };
            return base;
          }
          return base;
        });
      }
      setMessageInput('');
      setReplyingTo(null);
      
      // Stop typing indicator
      if (messagesSocketService && conversationId) {
        messagesSocketService.setTyping(conversationId, { isTyping: false });
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleSendImage = async (image) => {
    try {
      if (!conversationId) return;
      await MessageService.sendMediaMessage(conversationId, image, '', 'image');
    } catch (error) {
      console.error('Error sending image:', error);
    }
  };

  const handleSendGif = async (gifUrl) => {
    try {
      if (!conversationId) return;
      const width = undefined;
      const height = undefined;
      await MessageService.sendGifMessage(conversationId, gifUrl, width, height, '');
    } catch (error) {
      console.error('Error sending GIF:', error);
    }
  };

  const handleSendQuickReaction = async (emoji) => {
    try {
      if (!conversationId) return;
      await MessageService.sendMessage(conversationId, emoji, 'emoji');
    } catch (error) {
      console.error('Error sending quick reaction:', error);
    }
  };

  const handleReaction = async (messageId, emoji) => {
    try {
      if (!conversationId) return;
      await MessageService.addReaction(messageId, emoji, conversationId);
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const handleRemoveReaction = async (messageId, emoji) => {
    try {
      if (!conversationId) return;
      await MessageService.removeReaction(messageId, emoji, conversationId);
    } catch (error) {
      console.error('Error removing reaction:', error);
    }
  };

  const handlePinMessage = async (msg) => {
    if (!conversationId || !msg?.id) return;
    try {
      await MessageService.pinMessage(conversationId, msg.id);
      const pinsResp = await MessageService.getPinnedMessages(conversationId);
      const pins = Array.isArray(pinsResp?.pinned) ? pinsResp.pinned : [];
      setPinnedMessages(pins);
    } catch (error) {
      console.error('Error pinning message:', error);
    }
  };

  const handleUnpinMessage = async (msg) => {
    if (!conversationId || !msg?.id) return;
    try {
      await MessageService.unpinMessage(conversationId, msg.id);
      const pinsResp = await MessageService.getPinnedMessages(conversationId);
      const pins = Array.isArray(pinsResp?.pinned) ? pinsResp.pinned : [];
      setPinnedMessages(pins);
    } catch (error) {
      console.error('Error unpinning message:', error);
    }
  };

  const handleReply = (message) => {
    setReplyingTo(message);
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  // Handle audio call (supports both direct and group calls)
  const handleAudioCall = () => {
    if (!conversation) return;

    const isGroupCall = conversation.type !== 'direct';
    
    if (isGroupCall) {
      // Group call - get all participants except current user
      const otherParticipants = conversation.participants?.filter(p => {
        const candidateUserId = p?.user?.id || p?.participantId || p?.id;
        const isUserParticipant = !p?.participantType || p.participantType === 'user';
        return isUserParticipant && candidateUserId && candidateUserId !== user?.id;
      }) || [];

      if (otherParticipants.length === 0) {
        alert('No other participants in this conversation.');
        return;
      }

      const participantIds = otherParticipants.map(p => p?.user?.id || p?.participantId || p?.id);

      openCall({
        callId: `group_call_${Date.now()}`,
        recipientId: participantIds[0],
        recipientName: conversation.name || 'Group Call',
        recipientAvatar: conversation.avatar,
        conversationId: conversation.id,
        isIncoming: false,
        callType: 'audio',
        isGroupCall: true,
        participants: participantIds,
      });
    } else {
      // Direct call
      const otherParticipant = conversation.participants?.find(p => {
        const candidateUserId = p?.user?.id || p?.participantId || p?.id;
        const isUserParticipant = !p?.participantType || p.participantType === 'user';
        return isUserParticipant && candidateUserId && candidateUserId !== user?.id;
      });

      const recipientUserId = otherParticipant?.user?.id || otherParticipant?.participantId;
      if (!recipientUserId) {
        alert('Cannot initiate call. User information not available.');
        return;
      }

      openCall({
        callId: `call_${Date.now()}`,
        recipientId: recipientUserId,
        recipientName: conversation.name || otherParticipant?.user?.username,
        recipientAvatar: conversation.avatar || otherParticipant?.user?.profilePicture,
        conversationId: conversation.id,
        isIncoming: false,
        callType: 'audio',
      });
    }
  };

  // Handle video call (supports both direct and group calls)
  const handleVideoCall = () => {
    if (!conversation) return;

    const isGroupCall = conversation.type !== 'direct';
    
    if (isGroupCall) {
      // Group call - get all participants except current user
      const otherParticipants = conversation.participants?.filter(p => {
        const candidateUserId = p?.user?.id || p?.participantId || p?.id;
        const isUserParticipant = !p?.participantType || p.participantType === 'user';
        return isUserParticipant && candidateUserId && candidateUserId !== user?.id;
      }) || [];

      if (otherParticipants.length === 0) {
        alert('No other participants in this conversation.');
        return;
      }

      const participantIds = otherParticipants.map(p => p?.user?.id || p?.participantId || p?.id);

      openCall({
        callId: `group_call_${Date.now()}`,
        recipientId: participantIds[0],
        recipientName: conversation.name || 'Group Call',
        recipientAvatar: conversation.avatar,
        conversationId: conversation.id,
        isIncoming: false,
        callType: 'video',
        isGroupCall: true,
        participants: participantIds,
      });
    } else {
      // Direct call
      const otherParticipant = conversation.participants?.find(p => {
        const candidateUserId = p?.user?.id || p?.participantId || p?.id;
        const isUserParticipant = !p?.participantType || p.participantType === 'user';
        return isUserParticipant && candidateUserId && candidateUserId !== user?.id;
      });

      const recipientUserId = otherParticipant?.user?.id || otherParticipant?.participantId;
      if (!recipientUserId) {
        alert('Cannot initiate call. User information not available.');
        return;
      }

      openCall({
        callId: `call_${Date.now()}`,
        recipientId: recipientUserId,
        recipientName: conversation.name || otherParticipant?.user?.username,
        recipientAvatar: conversation.avatar || otherParticipant?.user?.profilePicture,
        conversationId: conversation.id,
        isIncoming: false,
        callType: 'video',
      });
    }
  };

  // Calculate position from right
  const rightPosition = 100 + (position * 340); // 320px width + 20px gap

  if (isMinimized) {
    return null; // Minimized state handled by ChatPopupBar
  }

  return (
    <Fade in={!isMinimized}>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          right: `${rightPosition}px`,
          width: 320,
          height: 450,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '8px 8px 0 0',
          overflow: 'hidden',
          zIndex: 1300,
          transition: 'all 0.3s ease',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 12px',
            backgroundColor: 'primary.main',
            color: 'white',
            cursor: 'pointer',
          }}
          onClick={onMinimize}
        >
          <Avatar
            src={getConversationAvatar()}
            sx={{ width: 32, height: 32, mr: 1 }}
          >
            {getConversationName()[0]}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {getConversationName()}
            </Typography>
            {Object.keys(typingUsers).length > 0 && (
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                typing...
              </Typography>
            )}
          </Box>
          <IconButton size="small" sx={{ color: 'white' }} onClick={handleAudioCall} title={conversation?.type === 'direct' ? 'Audio Call' : 'Group Audio Call'}>
            <CallIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: 'white' }} onClick={handleVideoCall} title={conversation?.type === 'direct' ? 'Video Call' : 'Group Video Call'}>
            <VideocamIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: 'white' }} onClick={onMinimize}>
            <MinimizeIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: 'white' }} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            padding: 2,
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <MiniProfile
                conversation={conversation}
                name={getConversationName()}
                avatarUrl={getConversationAvatar()}
              />
              <Typography variant="body2">No messages yet</Typography>
              <Typography variant="caption">Send a message to start chatting</Typography>
            </Box>
          ) : (
            <>
              {shouldShowMiniProfile && (
                <MiniProfile
                  conversation={conversation}
                  name={getConversationName()}
                  avatarUrl={getConversationAvatar()}
                />
              )}

              {groupedMessages.map((item, index) => {
                if (item.type === 'date') {
                  return (
                    <Box key={item.id || `date-${index}`} sx={{ display: 'flex', justifyContent: 'center', py: 0.75 }}>
                      <Box
                        sx={{
                          px: 1.25,
                          py: 0.25,
                          borderRadius: '12px',
                          backgroundColor: '#E5E5EA',
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                          {item.date}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }

                return (
                  <MessageBubble
                    key={item.id || `msg-${index}`}
                    message={item}
                    isFirstInGroup={item.isFirstInGroup}
                    isLastInGroup={item.isLastInGroup}
                    currentUserId={user?.id}
                    isPinned={pinnedMessageIdSet.has(item.id)}
                    onPin={() => handlePinMessage(item)}
                    onUnpin={() => handleUnpinMessage(item)}
                    onReply={() => handleReply(item)}
                    quickReactionEmoji={conversation?.quickReactionEmoji || '👍'}
                    showStatus={Boolean((item.clientId || item.id) && (item.clientId || item.id) === lastOutgoingMessageKey)}
                    onVisible={handleMessageVisible}
                    onReact={handleReaction}
                    onRemoveReaction={handleRemoveReaction}
                  />
                );
              })}
            </>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Reply Preview */}
        {replyingTo && (
          <Box
            sx={{
              px: 2,
              py: 1,
              backgroundColor: 'action.hover',
              borderTop: '1px solid',
              borderTopColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" fontWeight="600" color="primary">
                Replying to {replyingTo.sender?.profile?.displayName || replyingTo.sender?.username || 'User'}
              </Typography>
              <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.secondary' }}>
                {replyingTo.content}
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleCancelReply}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Message Input Area */}
        <MessageInput
          messageInput={messageInput}
          onMessageChange={handleMessageChange}
          onSendMessage={handleSendMessage}
          onSendImage={handleSendImage}
          onSendGif={handleSendGif}
          onSendQuickReaction={handleSendQuickReaction}
          quickReactionEmoji={conversation?.quickReactionEmoji || '👍'}
          compact={true}
        />
      </Paper>
    </Fade>
  );
};

export default ChatPopup;
